import React, { useCallback, useRef } from 'react'
import { useMutation } from 'react-query'
import { useMachine } from '@xstate/react'

import { FileUploaderMachineActor } from '../machines/FileUploaderMachine/FileUploaderMachine'
import { fileUploadersMachine } from '../machines/FileUploadersMachine/FileUploadersMachine'
import FileUploadConfiguration from '../stores/models/FileUploadConfiguration'
import { useFileUploadConfigServices } from './services/useFileUploadConfigServices'

type fileUploaderProviderState = {
  fileUploadersMachine?
  spawnNewFileUploader?: () => FileUploaderMachineActor
  getAwsCredentials?: () => void
  refreshAwsCredentials?: () => Promise<FileUploadConfiguration>
}

interface RefreshAuthTokensContextType {
  refreshTokenApi: any
  getAccessToken: () => string
  getRefreshToken: () => string
}

interface FileUploaderProps {
  children
  s3UploadBaseUrl?: string
  refreshAuthTokensHook?: () => RefreshAuthTokensContextType
}

const FileUploaderContext = React.createContext<fileUploaderProviderState>({})

export const useFileUploader = () => {
  const context = React.useContext(FileUploaderContext)

  if (!context)
    throw new Error('Expected to be wrapped in a FileUploaderProvider')

  return context
}

export const FileUploaderProvider = (props: FileUploaderProps) => {
  const { s3UploadBaseUrl, refreshAuthTokensHook, children } = props
  const { fileUploadConfigServices } = useFileUploadConfigServices(
    s3UploadBaseUrl,
    refreshAuthTokensHook
  )

  const { mutateAsync: getFileUploadConfig } = useMutation(() =>
    fileUploadConfigServices.getFileUploadConfig()
  )

  const fileUploader = useMachine(
    fileUploadersMachine.withConfig({
      services: {
        getAWSCredentials: () => getFileUploadConfig()
      }
    }),
    { devTools: true }
  )

  const [state, send] = fileUploader

  const getAwsCredentials = () => {
    send({ type: 'GET_AWS_CREDENTIALS' })
  }

  const inFlightRefreshRef = useRef<Promise<FileUploadConfiguration> | null>(
    null
  )

  const refreshAwsCredentials =
    useCallback((): Promise<FileUploadConfiguration> => {
      if (inFlightRefreshRef.current) return inFlightRefreshRef.current

      const promise = getFileUploadConfig()
        .then((data) => new FileUploadConfiguration(data))
        .finally(() => {
          inFlightRefreshRef.current = null
        })

      inFlightRefreshRef.current = promise
      return promise
    }, [getFileUploadConfig])

  return (
    <FileUploaderContext.Provider
      value={{
        fileUploadersMachine: fileUploader,
        getAwsCredentials,
        refreshAwsCredentials
      }}
    >
      {children}
    </FileUploaderContext.Provider>
  )
}
